import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { useAuth } from '@/lib/auth';
import { Loader, Award } from '@/components/ui/Icons';

type Entry = {
  id: number;
  admin_email: string;
  action: string;
  meta: Record<string, unknown> | null;
  quote_id: number | null;
  created_at: string;
};

type Resp = { ok: true; entries: Entry[]; total: number; page: number; per_page: number };

export default function SuperAudit() {
  const { user, loading } = useAuth();
  const { data, isLoading, isError } = useQuery<Resp>({
    queryKey: ['super-audit'],
    queryFn: () => api<Resp>('/admin/super/audit?per_page=100'),
    enabled: !!user?.is_super,
  });

  if (loading) return null;
  if (!user?.is_super) return <Navigate to="/admin/quotes" replace />;

  return (
    <>
      <Seo title="Audit log" description="Super-admin audit log" path="/admin/super/audit" noindex />

      <header className="mb-6">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
          <Award className="h-3 w-3" /> Super-admin
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900">Audit log</h1>
        <p className="mt-1 text-sm text-ink-700">
          Every admin action against a quote (status change, notes, deletes).
        </p>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-ink-700"><Loader className="h-5 w-5" /> Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700">Couldn&rsquo;t load.</div>
      ) : data.entries.length === 0 ? (
        <p className="rounded-md border border-paper-100 bg-paper-50 p-4 text-sm text-ink-700">
          No actions logged yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-paper-100 bg-paper-0 shadow-card">
          <table className="min-w-full divide-y divide-paper-100 text-sm">
            <thead className="bg-paper-50 text-left text-xs uppercase tracking-wider text-ink-700/80">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Quote</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-paper-100">
              {data.entries.map((e) => (
                <tr key={e.id} className="hover:bg-paper-50">
                  <td className="whitespace-nowrap px-4 py-3 text-ink-700">{new Date(e.created_at).toLocaleString('en-GB')}</td>
                  <td className="px-4 py-3 text-navy-900">{e.admin_email}</td>
                  <td className="px-4 py-3 text-navy-900">{prettyAction(e.action)}</td>
                  <td className="px-4 py-3">
                    {e.quote_id != null ? (
                      <Link to={`/admin/quotes/${e.quote_id}`} className="font-semibold text-gold-700 hover:text-gold-600">
                        #{e.quote_id}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-700">{describeMeta(e.action, e.meta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function prettyAction(a: string): string {
  return a.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function describeMeta(action: string, meta: Record<string, unknown> | null): string {
  if (!meta) return '—';
  if (action === 'status_change' && 'from' in meta && 'to' in meta) {
    return `${meta.from} → ${meta.to}`;
  }
  return Object.entries(meta).map(([k, v]) => `${k}: ${String(v)}`).join(' · ');
}
